/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import type { McpPlugin, McpTransport } from './types';

type LocalTransport = Extract<McpTransport, { kind: 'local' }>;

export const pluginName = (plugin: McpPlugin) => plugin.id.split('@')[0];

export const shellArg = (value: string) =>
  /^[A-Za-z0-9._~%/:@=+,-]+$/.test(value) ? value : `'${value.replace(/'/g, `'\\''`)}'`;

/** Install a plugin from its configured marketplace. */
export const claudePluginCommand = (p: McpPlugin) =>
  `claude plugin marketplace add ${shellArg(p.source)} && claude plugin install ${shellArg(p.id)}`;

/** The connector without the plugin, for the many people who can reach this
 * Backstage but NOT the m42-platform marketplace the plugin is published to —
 * a private repo, so `plugin marketplace add` fails for them and the plugin
 * path is a dead end. `--transport http` is required (the CLI defaults to
 * stdio) and `--scope user` is deliberate: the default is `local`, which would
 * bind the server to whichever directory the command happened to run in.
 * Verified against `claude mcp add --help` 2026-09-08. */
export const claudeMcpCommand = (name: string, url: string) =>
  `claude mcp add --transport http --scope user ${shellArg(name)} ${shellArg(url)}`;

/** Codex reads the Claude plugin format as-is (.claude-plugin, .mcp.json,
 * skills), so the same marketplace installs the plugin. Unlike `mcp add`, the
 * plugin install does not start OAuth — `codex mcp login` has to follow.
 * Both paths verified with codex-cli 0.156.1 on 2026-09-24. */
export const codexPluginCommand = (p: McpPlugin) =>
  `codex plugin marketplace add ${shellArg(p.source)} && codex plugin add ${shellArg(p.id)}`;

/** Detects the server's OAuth and signs in on the spot (DCR, scope
 * `openid offline_access`), so no separate login. */
export const codexAddCommand = (name: string, url: string) =>
  `codex mcp add ${shellArg(name)} --url ${shellArg(url)}`;

export const cursorSnippet = (name: string, url: string) =>
  `{ "mcpServers": { ${JSON.stringify(name)}: { "url": ${JSON.stringify(url)} } } }`;
export const opencodeSnippet = (name: string, url: string) =>
  `{ "mcp": { ${JSON.stringify(name)}: { "type": "remote", "url": ${JSON.stringify(url)}, "enabled": true } } }`;

/** Claude Code's deep link opens a terminal session with `q` PRE-FILLED — it
 * never sends or executes anything — so the install request rides inside the
 * prompt; the user presses Enter and then approves the Bash command. Deliberately
 * no `cwd`/`repo`: the plugin is user-scoped, and a repo slug falls back to
 * $HOME for anyone without a local clone. Claude Code registers the scheme only
 * after its first interactive prompt — before that the click is a no-op (the
 * tab's `note` says so). Observed working 2026-09-04. */
export const claudeCliLink = (prompt: string) =>
  `claude-cli://open?q=${encodeURIComponent(prompt)}`;

/** The ChatGPT app maps this to `codex://new?prompt=` and opens a new Codex
 * chat with `q` PREFILLED, never sent — the same contract as
 * the Claude Code link. The https form over the bare scheme so a reader
 * without the app gets chatgpt.com's "Opening ChatGPT" page with a download
 * button instead of a dead click. There is no MCP-install route, and
 * `codex://plugins/install/…` resolves only marketplaces already configured
 * (a no-op when tried), so a prefilled command is the one path.
 *
 * Both links verified end to end 2026-09-24 from a reset Codex: one approval
 * (write ~/.codex + network, for the turn), then the sandbox cannot open a
 * browser, so Codex posts the authorize link in the chat to click. */
export const codexOpenLink = (prompt: string) =>
  `https://chatgpt.com/codex/open-app?q=${encodeURIComponent(prompt)}`;

/** Cursor's documented MCP install deeplink: `config` is base64 of the ONE
 * server object (not the mcpServers map), padding URL-encoded. Cursor parses
 * `type: 'http'` as "Remote HTTPS" in its Install MCP server? dialog
 * (observed 2026-09-04) and then runs the same DCR + OAuth as a manual add. */
const cursorConfigLink = (name: string, config: object) =>
  `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(name)}&config=${encodeURIComponent(
    btoa(Array.from(new TextEncoder().encode(JSON.stringify(config)), byte => String.fromCharCode(byte)).join('')),
  )}`;
export const cursorInstallLink = (name: string, url: string) => cursorConfigLink(name, { type: 'http', url });

/* Local (stdio) servers, PLAT-2691. The catalog only admits a bare command and
 * plain argv words (mcpCatalogProcessor), so `shellArg` never has to quote them
 * — it stays for defence in depth. */

const localArgv = (t: LocalTransport) => [t.command, ...t.args].map(shellArg).join(' ');

/** Only env entries with a catalog value reach a snippet; the rest are the user's to set. */
export const localEnv = (t: LocalTransport): Record<string, string> =>
  Object.fromEntries((t.env ?? []).flatMap(e => (e.value === undefined ? [] : [[e.name, e.value]])));

const envFlags = (t: LocalTransport) =>
  Object.entries(localEnv(t)).map(([key, value]) => ` --env ${shellArg(`${key}=${value}`)}`).join('');

/** stdio is the CLI's default transport, so no --transport flag; `--scope user`
 * for the same reason as the remote add. `--env` is variadic (`<env...>`), so it
 * sits after the name and before `--`, where it cannot swallow either. Verified
 * against `claude mcp add --help` and a real add + tool call, Claude Code 2.1.287,
 * 2026-10-14. */
export const claudeLocalCommand = (name: string, t: LocalTransport) =>
  `claude mcp add --scope user ${shellArg(name)}${envFlags(t)} -- ${localArgv(t)}`;

/** `codex mcp add <NAME> [--env KEY=VALUE] -- <COMMAND>...`; verified against
 * codex-cli 0.155 `mcp add --help` and a real add + tool call 2026-10-14. */
export const codexLocalCommand = (name: string, t: LocalTransport) =>
  `codex mcp add ${shellArg(name)}${envFlags(t)} -- ${localArgv(t)}`;

/** The `{ command, args, env }` server object shared by Cursor's mcp.json (and
 * its install deeplink) and Claude Desktop's claude_desktop_config.json — per
 * cursor.com/docs/context/mcp/install-links and modelcontextprotocol.io's
 * connect-local-servers guide, read 2026-10-14. */
export const localServerConfig = (t: LocalTransport) => {
  const env = localEnv(t);
  return { command: t.command, args: t.args, ...(Object.keys(env).length ? { env } : {}) };
};

const inlineJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(inlineJson).join(', ')}]`;
  if (value !== null && typeof value === 'object') {
    return `{ ${Object.entries(value).map(([k, v]) => `${JSON.stringify(k)}: ${inlineJson(v)}`).join(', ')} }`;
  }
  return JSON.stringify(value);
};

export const mcpServersLocalSnippet = (name: string, t: LocalTransport) =>
  inlineJson({ mcpServers: { [name]: localServerConfig(t) } });

/** OpenCode's local shape: `command` is ONE array (program + args) and env is
 * `environment` — per opencode.ai/docs/mcp-servers, read 2026-10-14, and a real
 * add + tool call with opencode 1.18.35. */
export const opencodeLocalSnippet = (name: string, t: LocalTransport) => {
  const env = localEnv(t);
  return inlineJson({ mcp: { [name]: {
    type: 'local', command: [t.command, ...t.args], enabled: true,
    ...(Object.keys(env).length ? { environment: env } : {}),
  } } });
};

/** Cursor's deeplink with the same stdio server object, per the install-links
 * doc. Not yet observed in the app (no Cursor on the verifying machine). */
export const cursorLocalInstallLink = (name: string, t: LocalTransport) => cursorConfigLink(name, localServerConfig(t));
